import React, { createContext, useContext, useState, useEffect } from "react";
import { loadSettings, parseTrainingDates, parseSessionsWithTimes, DEFAULT_SETTINGS } from "../lib/settings";

const SettingsContext = createContext(null);

export const DEFAULT_SEASON_CLOSED_MESSAGE =
  "The pool is now closed for the season. We look forward to welcoming you back next summer — details of the new season will appear here.";

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [maintenance, setMaintenance] = useState(false);

  useEffect(() => {
    async function fetchSettings() {
      try {
        const data = await loadSettings();
        setSettings(data);
      } catch (err) {
        console.error("Error loading settings:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchSettings();
  }, []);

  useEffect(() => {
    async function fetchMaintenance() {
      try {
        const res = await fetch("/api/maintenance");
        if (!res.ok) return;
        const data = await res.json();
        setMaintenance(!!data.maintenance);
      } catch (err) {
        console.error("Error loading maintenance status:", err);
      }
    }
    fetchMaintenance();
  }, []);

  const getFirstOfficerTrainingDay = () => {
    return settings?.first_officer_training_day || DEFAULT_SETTINGS.first_officer_training_day;
  };

  const getFirstOfficerPoolSessions = () => {
    return parseSessionsWithTimes(settings?.first_officer_pool_sessions || DEFAULT_SETTINGS.first_officer_pool_sessions);
  };

  const getTheorySessions = () => {
    return parseSessionsWithTimes(settings?.second_officer_theory_sessions || DEFAULT_SETTINGS.second_officer_theory_sessions);
  };

  const getPoolSessions = () => {
    return parseSessionsWithTimes(settings?.second_officer_pool_sessions || DEFAULT_SETTINGS.second_officer_pool_sessions);
  };

  const getSeasonConfig = () => ({
    REGISTRATION_OPEN_DATE: settings?.registration_open_date || DEFAULT_SETTINGS.registration_open_date,
    SEASON_START: settings?.season_start || DEFAULT_SETTINGS.season_start,
    SEASON_END: settings?.season_end || DEFAULT_SETTINGS.season_end,
    EVENING_TIMES_CHANGE_DATE: settings?.evening_times_change_date || DEFAULT_SETTINGS.evening_times_change_date,
    REGISTRATION_YEAR: settings?.registration_year || DEFAULT_SETTINGS.registration_year,
  });

  const isDevEnvironment = () => {
    const host = window.location.hostname;
    return host.includes('.ts.net') ||
           host === 'localhost' ||
           host === '127.0.0.1';
  };

  const isRegistrationOpen = () => {
    if (isDevEnvironment()) {
      return true;
    }
    const openDate = settings?.registration_open_date || DEFAULT_SETTINGS.registration_open_date;
    return new Date() >= new Date(openDate);
  };

  const getDutyFreezeStatus = () => {
    const now = new Date();
    const firstFreeze = settings?.first_rota_freeze_date || DEFAULT_SETTINGS.first_rota_freeze_date;
    const secondFreeze = settings?.second_rota_freeze_date || DEFAULT_SETTINGS.second_rota_freeze_date;
    const seasonEnd = settings?.season_end || DEFAULT_SETTINGS.season_end;

    const firstFreezeDate = firstFreeze ? new Date(firstFreeze) : null;
    const secondFreezeDate = secondFreeze ? new Date(secondFreeze) : null;
    const seasonEndDate = seasonEnd ? new Date(seasonEnd) : null;

    if (secondFreezeDate && now >= secondFreezeDate) {
      return { frozen: true, frozenFrom: secondFreezeDate, frozenUntil: seasonEndDate, fullFreeze: true };
    }

    if (firstFreezeDate && now >= firstFreezeDate) {
      return { frozen: true, frozenFrom: firstFreezeDate, frozenUntil: secondFreezeDate || seasonEndDate, fullFreeze: false };
    }

    return { frozen: false, frozenFrom: null, frozenUntil: null, fullFreeze: false };
  };

  const getSafetyOfficerDates = () => ({
    FIRST_OFFICER: {
      TRAINING_DAY: getFirstOfficerTrainingDay(),
      POOL_SESSIONS: getFirstOfficerPoolSessions(),
      REGISTRATION_DEADLINE: settings?.first_officer_deadline || DEFAULT_SETTINGS.first_officer_deadline,
      DISCOUNT_PERCENTAGE: parseInt(settings?.first_officer_discount) || parseInt(DEFAULT_SETTINGS.first_officer_discount) || 50,
    },
    SECOND_OFFICER: {
      THEORY_SESSIONS: getTheorySessions(),
      POOL_SESSIONS: getPoolSessions(),
      REGISTRATION_DEADLINE: settings?.second_officer_deadline || DEFAULT_SETTINGS.second_officer_deadline,
    },
  });

  const isFirstSafetyOfficerDeadlinePassed = () => {
    const deadline = settings?.first_officer_deadline || DEFAULT_SETTINGS.first_officer_deadline;
    return new Date() > new Date(deadline);
  };

  const isSecondSafetyOfficerDeadlinePassed = () => {
    const deadline = settings?.second_officer_deadline || DEFAULT_SETTINGS.second_officer_deadline;
    return new Date() > new Date(deadline);
  };

  // Parse date string like "1 March 26" or "8 March 26 (10am-12pm)" to a Date object
  const parseDateFromString = (dateStr) => {
    if (!dateStr) return null;
    // Remove time in parentheses if present
    const cleanDate = dateStr.replace(/\s*\([^)]*\)/, '').trim();
    // Parse "1 March 26" format
    const match = cleanDate.match(/^(\d{1,2})\s+(\w+)\s+(\d{2})$/);
    if (match) {
      const [, day, month, year] = match;
      const fullYear = 2000 + parseInt(year);
      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                          'July', 'August', 'September', 'October', 'November', 'December'];
      const monthIndex = monthNames.findIndex(m => m.toLowerCase().startsWith(month.toLowerCase()));
      if (monthIndex !== -1) {
        return new Date(fullYear, monthIndex, parseInt(day), 23, 59, 59); // End of the day
      }
    }
    return null;
  };

  // Check if the 1st officer training day has passed
  const isFirstOfficerTrainingDayPassed = () => {
    const trainingDay = settings?.first_officer_training_day || DEFAULT_SETTINGS.first_officer_training_day;
    const trainingDate = parseDateFromString(trainingDay);
    if (!trainingDate) return false;
    return new Date() > trainingDate;
  };

  // Check if all 1st officer pool sessions have passed
  const isLastFirstOfficerPoolSessionPassed = () => {
    const poolSessions = getFirstOfficerPoolSessions();
    if (!poolSessions || poolSessions.length === 0) return true;
    
    // Find the latest pool session date
    let latestDate = null;
    for (const session of poolSessions) {
      const sessionDate = parseDateFromString(session.DATE);
      if (sessionDate && (!latestDate || sessionDate > latestDate)) {
        latestDate = sessionDate;
      }
    }
    
    if (!latestDate) return false;
    return new Date() > latestDate;
  };

  // Check if 1st officer training is still available (training day or pool sessions in future)
  const isFirstOfficerTrainingAvailable = () => {
    return !isFirstOfficerTrainingDayPassed() || !isLastFirstOfficerPoolSessionPassed();
  };

  // Check if all 2nd officer theory sessions have passed
  const isLastTheorySessionPassed = () => {
    const theorySessions = getTheorySessions();
    if (!theorySessions || theorySessions.length === 0) return true;
    
    // Find the latest theory session date
    let latestDate = null;
    for (const session of theorySessions) {
      const sessionDate = parseDateFromString(session.DATE);
      if (sessionDate && (!latestDate || sessionDate > latestDate)) {
        latestDate = sessionDate;
      }
    }
    
    if (!latestDate) return false;
    return new Date() > latestDate;
  };

  const isLastSecondOfficerPoolSessionPassed = () => {
    const sessions = getPoolSessions();
    if (!sessions || sessions.length === 0) return true;
    let latestDate = null;
    for (const session of sessions) {
      const sessionDate = parseDateFromString(session.DATE);
      if (sessionDate && (!latestDate || sessionDate > latestDate)) {
        latestDate = sessionDate;
      }
    }
    if (!latestDate) return false;
    return new Date() > latestDate;
  };

  const getOpeningTimes = () => ({
    REGULAR: {
      EVENING: {
        START: settings?.evening_start || DEFAULT_SETTINGS.evening_start,
        END: settings?.evening_end || DEFAULT_SETTINGS.evening_end,
        LATE_SEASON_END: settings?.evening_late_season_end || DEFAULT_SETTINGS.evening_late_season_end,
      },
      WEEKEND: {
        START: settings?.weekend_start || DEFAULT_SETTINGS.weekend_start,
        END: settings?.weekend_end || DEFAULT_SETTINGS.weekend_end,
      },
    },
    SCHOOL_HOLIDAYS: {
      START: settings?.school_holiday_start || DEFAULT_SETTINGS.school_holiday_start,
      END: settings?.school_holiday_end || DEFAULT_SETTINGS.school_holiday_end,
    },
  });

  // End-of-season switch: swaps the home-page opening times for a closed notice
  // and turns off member login (the server enforces the login half). Admins are
  // unaffected. Booleans arrive from the DB as true/false but tolerate "true".
  const isSeasonClosed = () => {
    const value = settings?.season_closed;
    return value === true || value === "true";
  };

  const getSeasonClosedMessage = () => {
    const custom = (settings?.season_closed_message || "").trim();
    return custom || DEFAULT_SEASON_CLOSED_MESSAGE;
  };

  const getPricingConfig = () => ({
    CUTOFF_DATE: settings?.price_cutoff_date || DEFAULT_SETTINGS.price_cutoff_date,
    INDIVIDUAL_EARLY: parseInt(settings?.individual_price_early) || parseInt(DEFAULT_SETTINGS.individual_price_early),
    INDIVIDUAL_LATE: parseInt(settings?.individual_price_late) || parseInt(DEFAULT_SETTINGS.individual_price_late),
    FAMILY_EARLY: parseInt(settings?.family_price_early) || parseInt(DEFAULT_SETTINGS.family_price_early),
    FAMILY_LATE: parseInt(settings?.family_price_late) || parseInt(DEFAULT_SETTINGS.family_price_late),
    FIRST_OFFICER_DISCOUNT: parseInt(settings?.first_officer_discount) || parseInt(DEFAULT_SETTINGS.first_officer_discount),
  });

  const isPriceCutoffPassed = () => {
    const cutoffDate = settings?.price_cutoff_date || DEFAULT_SETTINGS.price_cutoff_date;
    return new Date() >= new Date(cutoffDate);
  };

  const value = {
    settings,
    loading,
    maintenance,
    getSeasonConfig,
    getSafetyOfficerDates,
    getOpeningTimes,
    getPricingConfig,
    getFirstOfficerTrainingDay,
    getFirstOfficerPoolSessions,
    getTheorySessions,
    getPoolSessions,
    isFirstSafetyOfficerDeadlinePassed,
    isSecondSafetyOfficerDeadlinePassed,
    isFirstOfficerTrainingDayPassed,
    isFirstOfficerTrainingAvailable,
    isLastFirstOfficerPoolSessionPassed,
    isLastTheorySessionPassed,
    isLastSecondOfficerPoolSessionPassed,
    isRegistrationOpen,
    isPriceCutoffPassed,
    getDutyFreezeStatus,
    isSeasonClosed,
    getSeasonClosedMessage,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error("useSettings must be used within a SettingsProvider");
  }
  return context;
}

export default SettingsContext;
